import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  ToggleButton,
  Typography,
} from "@mui/material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import type { AuthTokenPayload } from "../../auth/types";
import type { RoleOrgType, CanonicalRole } from "../../auth/role-templates";
import {
  CANONICAL_ROLE_LABELS,
  expandRoleForSites,
} from "../../auth/role-templates";
import {
  actionsForEntity,
  buildPermissionCandidates,
  buildFullCatalogForOrg,
  entityDisplayName,
  entityDescription,
  entityGroupDescription,
  entityGroupsForOrg,
  actionDisplayName,
  entityPermissionSet,
  type CatalogEntity,
} from "../../auth/permission-catalog";
import { hasPermission } from "../../auth/matching";
import { filterGrantablePermissions } from "../../auth/user-management";
import { editorSiteIdForCollectionSites } from "../../auth/permission-sites";
import type { PermissionAction } from "../../auth/types";

interface AdvancedPermissionsEditorProps {
  orgId: string;
  orgType: RoleOrgType;
  siteIds: string[] | "*";
  selected: string[];
  onChange: (permissions: string[]) => void;
  requesterPerms: readonly string[];
  tokenPayload: AuthTokenPayload | null;
  disabled?: boolean;
}

const ACTION_ICONS: Partial<Record<string, React.ReactElement>> = {
  list: <FormatListBulletedIcon sx={{ fontSize: 16 }} />,
  read: <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />,
  create: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />,
  write: <EditOutlinedIcon sx={{ fontSize: 16 }} />,
  delete: <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
  manage: <SettingsOutlinedIcon sx={{ fontSize: 16 }} />,
  assign: <PersonAddOutlinedIcon sx={{ fontSize: 16 }} />,
  accept: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
  reject: <CancelOutlinedIcon sx={{ fontSize: 16 }} />,
  submit: <SendOutlinedIcon sx={{ fontSize: 16 }} />,
};

function isPermissionSelected(selected: Set<string>, perm: string): boolean {
  if (selected.has(perm)) return true;
  for (const s of selected) {
    if (hasPermission([s], perm)) return true;
  }
  return false;
}

function resolvePresetToCatalog(
  patterns: readonly string[],
  catalog: readonly string[],
): string[] {
  const resolved = catalog.filter((p) => patterns.some((pattern) => hasPermission([pattern], p)));
  return resolved.length > 0 ? resolved : [...patterns];
}

function removePermissionFromSelection(selected: Set<string>, perm: string, catalog: readonly string[]) {
  if (selected.has(perm)) {
    selected.delete(perm);
    return;
  }
  const covering = [...selected].filter((s) => s !== perm && hasPermission([s], perm));
  for (const wild of covering) {
    selected.delete(wild);
    for (const candidate of catalog) {
      if (hasPermission([wild], candidate) && candidate !== perm) {
        selected.add(candidate);
      }
    }
  }
}

export const AdvancedPermissionsEditor: React.FC<AdvancedPermissionsEditorProps> = ({
  orgId,
  orgType,
  siteIds,
  selected,
  onChange,
  requesterPerms,
  tokenPayload,
  disabled = false,
}) => {
  const siteId = editorSiteIdForCollectionSites(siteIds);

  const expandSiteIds = useMemo((): string[] | "*" => {
    if (siteIds === "*") return "*";
    if (siteIds.length === 0) return "*";
    return siteIds;
  }, [siteIds]);
  const fullCatalog = useMemo(
    () => buildFullCatalogForOrg(orgId, orgType, siteId),
    [orgId, orgType, siteId],
  );

  const grantableCatalog = useMemo(
    () => filterGrantablePermissions(requesterPerms, fullCatalog, tokenPayload),
    [requesterPerms, fullCatalog, tokenPayload],
  );

  const grantableSet = useMemo(() => new Set(grantableCatalog), [grantableCatalog]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const entities = useMemo(() => {
    const seen = new Set<CatalogEntity>();
    for (const perm of grantableCatalog) {
      const parts = perm.split(".");
      if (parts.length >= 4 && parts[0] === "recyclee" && parts[1] === orgId) {
        seen.add(parts[3] as CatalogEntity);
      }
    }
    return seen;
  }, [grantableCatalog, orgId]);

  const groups = useMemo(() => {
    return entityGroupsForOrg(orgType)
      .map((group) => ({
        ...group,
        entities: group.entities.filter((e) => entities.has(e)),
      }))
      .filter((g) => g.entities.length > 0);
  }, [orgType, entities]);

  const updateSelection = (next: Set<string>) => {
    onChange([...next]);
  };

  const togglePermission = (perm: string) => {
    if (!grantableSet.has(perm)) return;
    const next = new Set(selected);
    if (isPermissionSelected(next, perm)) {
      removePermissionFromSelection(next, perm, grantableCatalog);
    } else {
      next.add(perm);
    }
    updateSelection(next);
  };

  const toggleEntityAll = (entity: CatalogEntity) => {
    const allPerms = entityPermissionSet(orgId, siteId, entity).filter((p) =>
      grantableSet.has(p),
    );
    const concretePerms = allPerms.filter((p) => !p.endsWith(".*"));
    const next = new Set(selected);
    const allSelected = concretePerms.every((p) => isPermissionSelected(next, p));
    if (allSelected) {
      for (const p of allPerms) {
        removePermissionFromSelection(next, p, grantableCatalog);
      }
    } else {
      for (const p of concretePerms) {
        next.add(p);
      }
      const wildcard = `recyclee.${orgId}.${siteId}.${entity}.*`;
      next.delete(wildcard);
    }
    updateSelection(next);
  };

  const toggleEntityAction = (entity: CatalogEntity, action: PermissionAction) => {
    const perm = buildPermissionCandidates(orgId, siteId, entity, [action])[0];
    if (!grantableSet.has(perm)) return;
    const next = new Set(selected);
    if (isPermissionSelected(next, perm)) {
      removePermissionFromSelection(next, perm, grantableCatalog);
    } else {
      next.add(perm);
      const entityActions = actionsForEntity(entity);
      if (action === "read" && entityActions.includes("list")) {
        next.add(buildPermissionCandidates(orgId, siteId, entity, ["list"])[0]);
      }
      if (action === "write") {
        if (entityActions.includes("list")) {
          next.add(buildPermissionCandidates(orgId, siteId, entity, ["list"])[0]);
        }
        if (entityActions.includes("read")) {
          next.add(buildPermissionCandidates(orgId, siteId, entity, ["read"])[0]);
        }
      }
    }
    updateSelection(next);
  };

  const isEntityAllSelected = (entity: CatalogEntity): boolean => {
    const concrete = entityPermissionSet(orgId, siteId, entity)
      .filter((p) => grantableSet.has(p) && !p.endsWith(".*"));
    if (!concrete.length) return false;
    return concrete.every((p) => isPermissionSelected(selectedSet, p));
  };

  const applyPreset = (role: CanonicalRole) => {
    const expanded = expandRoleForSites(orgType, role, orgId, expandSiteIds);
    const grantablePatterns = filterGrantablePermissions(requesterPerms, expanded, tokenPayload);
    onChange(resolvePresetToCatalog(grantablePatterns, grantableCatalog));
  };

  const renderEntityCard = (entity: CatalogEntity) => {
    const actions = actionsForEntity(entity).filter((action) => {
      const perm = buildPermissionCandidates(orgId, siteId, entity, [action])[0];
      return grantableSet.has(perm);
    });
    const allSelected = isEntityAllSelected(entity);
    const hasWildcard = entityPermissionSet(orgId, siteId, entity).some(
      (p) => p.endsWith(".*") && grantableSet.has(p),
    );

    return (
      <Grid item xs={12} sm={6} lg={4} key={entity}>
        <Card
          variant="outlined"
          sx={{
            height: "100%",
            borderColor: allSelected ? "primary.main" : "divider",
            borderWidth: allSelected ? 2 : 1,
            bgcolor: allSelected ? "action.selected" : "background.paper",
            transition: "border-color 0.2s, background-color 0.2s",
          }}
        >
          <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3}>
              {entityDisplayName(entity)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, mb: 2, minHeight: 40, lineHeight: 1.45 }}
            >
              {entityDescription(entity)}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
              {hasWildcard && actions.length > 1 && (
                <ToggleButton
                  value="all"
                  selected={allSelected}
                  disabled={disabled}
                  onClick={() => toggleEntityAll(entity)}
                  size="small"
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    gap: 0.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    border: "1px solid",
                    borderColor: allSelected ? "primary.main" : "divider",
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      "&:hover": { bgcolor: "primary.dark" },
                    },
                  }}
                >
                  <DoneAllIcon sx={{ fontSize: 16 }} />
                  All
                </ToggleButton>
              )}
              {actions.map((action) => {
                const perm = buildPermissionCandidates(orgId, siteId, entity, [action])[0];
                const active = isPermissionSelected(selectedSet, perm);
                return (
                  <ToggleButton
                    key={action}
                    value={action}
                    selected={active}
                    disabled={disabled}
                    onClick={() => toggleEntityAction(entity, action)}
                    size="small"
                    sx={{
                      px: 1.25,
                      py: 0.5,
                      gap: 0.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "0.8125rem",
                      fontWeight: active ? 600 : 400,
                      border: "1px solid",
                      borderColor: active ? "primary.main" : "divider",
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.dark" },
                      },
                      "&:hover": {
                        bgcolor: active ? "primary.dark" : "action.hover",
                      },
                    }}
                  >
                    {ACTION_ICONS[action]}
                    {actionDisplayName(action)}
                  </ToggleButton>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Typography variant="body2" color="text.secondary">
          Grant ceiling: {grantableCatalog.length}/{fullCatalog.length} grantable
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {(Object.keys(CANONICAL_ROLE_LABELS) as CanonicalRole[]).map((role) => (
            <Button
              key={role}
              size="small"
              variant="outlined"
              disabled={disabled}
              onClick={() => applyPreset(role)}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              {CANONICAL_ROLE_LABELS[role]}
            </Button>
          ))}
        </Stack>
      </Stack>

      {groups.map((group) => {
        const description = entityGroupDescription(group.label);
        return (
          <Box key={group.label} sx={{ mb: 3.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.25 }}>
              {group.label}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {description}
              </Typography>
            )}
            <Grid container spacing={2}>
              {group.entities.map((entity) => renderEntityCard(entity))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

export default AdvancedPermissionsEditor;
